import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Radio, GraduationCap, Video, Clock, Award,
  CheckCircle2, ExternalLink, XCircle, Mail, FileDown, ArrowLeft, CalendarPlus, ChevronDown,
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";
import { BrandMark } from "@/components/BrandMark";
import { RichText } from "@/components/RichText";

export default function CourseAccess() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/access/${token}`); setData(data); }
      catch { setError(true); }
    })();
  }, [token]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="p-8 text-center max-w-md">
        <div className="h-14 w-14 rounded-full bg-red-100 dark:bg-red-500/15 flex items-center justify-center mx-auto mb-4"><XCircle className="h-8 w-8 text-red-600" /></div>
        <h2 className="font-heading text-xl font-bold">Access link not valid</h2>
        <p className="text-sm text-muted-foreground mt-2">This link is invalid or the payment hasn't been confirmed yet.</p>
      </Card>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const isLive = data.course_type === "live";

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border backdrop-blur-md bg-white/90 dark:bg-slate-900/90">
        <div className="max-w-3xl mx-auto h-full flex items-center justify-between px-4 sm:px-6 gap-2.5">
          <BrandMark />
          {data.is_member ? (
            <Link to={`/members?email=${encodeURIComponent(data.candidate_email)}`} data-testid="access-back-members-button">
              <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to my courses</Button>
            </Link>
          ) : (
            <Link to="/shop" data-testid="access-back-shop-button">
              <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1.5" />Return to course shop</Button>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 stagger-in">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" />Enrolment confirmed for {data.candidate_name}
          </div>
          <div className="flex flex-wrap gap-2">
            {data.has_certificate && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/access/${token}/certificate`} target="_blank" rel="noopener noreferrer" data-testid="download-certificate-button">
                <Button size="sm"><FileDown className="h-4 w-4 mr-1.5" />Download certificate</Button>
              </a>
            )}
            {data.chosen_session?.start && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/access/${token}/calendar.ics`} data-testid="access-add-calendar-button">
                <Button variant="outline" size="sm"><CalendarPlus className="h-4 w-4 mr-1.5" />Add to calendar</Button>
              </a>
            )}
            {data.has_invoice && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/access/${token}/invoice`} data-testid="download-invoice-button">
                <Button variant="outline" size="sm"><FileDown className="h-4 w-4 mr-1.5" />Download invoice (PDF)</Button>
              </a>
            )}
          </div>
        </div>
        <Badge className={`border-0 text-white mb-3 ${isLive ? "bg-red-500 hover:bg-red-500" : "bg-emerald-600 hover:bg-emerald-600"}`}>
          {isLive ? <><Radio className="h-3 w-3 mr-1" />LIVE TRAINING</> : <><GraduationCap className="h-3 w-3 mr-1" />SELF-PACED CPD</>}
        </Badge>
        <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="access-course-title">{data.title}</h1>
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{data.duration_hours}h</span>
          {data.cpd_points > 0 && <span className="flex items-center gap-1"><Award className="h-4 w-4" />{data.cpd_points} CPD points</span>}
        </div>
        {data.synopsis && <p className="text-base text-muted-foreground mt-4 leading-relaxed" data-testid="access-synopsis">{data.synopsis}</p>}
        {data.description && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowInfo(!showInfo)} data-testid="access-toggle-info-button">
              <ChevronDown className={`h-4 w-4 mr-1.5 transition-transform ${showInfo ? "rotate-180" : ""}`} />{showInfo ? "Hide course info" : "View full course info"}
            </Button>
            {showInfo && <RichText html={data.description} className="text-base text-foreground/90 mt-4 leading-relaxed border-l-2 border-primary/30 pl-4" testid="access-description" />}
          </div>
        )}

        {isLive ? (
          <Card className="p-6 mt-8 border-l-4 border-l-red-500">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Video className="h-5 w-5 text-red-500" />Join your live session</h3>
            <p className="text-sm text-muted-foreground mt-1.5">Use the button below at your scheduled session time. Keep this link safe — it's your personal access to the session.</p>
            <a href={data.join_link} target="_blank" rel="noopener noreferrer">
              <Button className="mt-4" data-testid="join-live-session-button"><Video className="h-4 w-4 mr-2" />Join live session <ExternalLink className="h-4 w-4 ml-2" /></Button>
            </a>
            <p className="text-xs text-muted-foreground mt-3 font-mono break-all">{data.join_link}</p>
            {data.chosen_session && (
              <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Your session date</p><p className="font-medium">{fmtDate(data.chosen_session.start)}</p></div>
                <div><p className="text-xs text-muted-foreground">Time</p><p className="font-medium">{fmtTime(data.chosen_session.start)} – {fmtTime(data.chosen_session.end)}</p></div>
                <div><p className="text-xs text-muted-foreground">Location</p><p className="font-medium">{data.chosen_session.location}</p></div>
                <div><p className="text-xs text-muted-foreground">Format</p><p className="font-medium capitalize">{data.chosen_session.mode}</p></div>
              </div>
            )}
          </Card>
        ) : (
          <Card className="p-6 mt-8">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Mail className="h-5 w-5 text-emerald-600" />Your course link is on its way</h3>
            <div className="mt-4 flex items-start gap-3 p-4 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10" data-testid="cpd-link-pending-message">
              <Mail className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Your personal course link will be emailed to you shortly.</p>
                <p className="text-sm text-muted-foreground mt-1">We send an individual access link for each CPD course once your payment has been received. Please check your inbox (and spam folder) at <span className="font-medium text-foreground">{data.candidate_email}</span>.</p>
              </div>
            </div>
            {data.objectives && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Learning objectives</p>
                <p className="text-sm">{data.objectives}</p>
              </div>
            )}
          </Card>
        )}

        {data.handouts_link && (
          <Card className="p-6 mt-6 border-l-4 border-l-emerald-500" data-testid="access-handouts-card">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><FileDown className="h-5 w-5 text-emerald-600" />Course handouts</h3>
            <p className="text-sm text-muted-foreground mt-1.5">Thank you for attending. Your handouts are ready to download for your records.</p>
            <a href={data.handouts_link} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="mt-4" data-testid="access-handouts-button"><FileDown className="h-4 w-4 mr-2" />Open handouts <ExternalLink className="h-4 w-4 ml-2" /></Button>
            </a>
          </Card>
        )}
      </main>
    </div>
  );
}
